// @ts-nocheck
import React, { useState } from "react";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { formatNumber, cn } from "@/lib/utils";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export function formatProcessTime(dateStr?: string | null): string {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    return format(d, "dd/MM HH:mm");
  } catch {
    return "—";
  }
}
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DatePeriodFilter,
  useDatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";
import { ListPageShell } from "@/components/shared/ListPageShell";
import { useHeaderSearch } from "@/hooks/useHeaderSearch";
import { ProductionLogForm, ProductionLogPrefill } from "@/pages/factory/production-logs/components/ProductionLogForm";
import { MachineAssignmentModal } from "./components/MachineAssignmentModal";
import { ProcessStatusBadge } from "./components/ProcessStatusBadge";
import { PrintModal } from "@/components/planning/print/PrintModal";
import { ProcessPlanningPrintContent } from "@/components/planning/print/ProcessPlanningPrintContent";
import { useAuthStore } from "@/stores/auth.store";
import {
  Zap,
  RefreshCw,
  Printer,
  Calendar,
  Layers,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  PlayCircle,
  PauseCircle,
  SlidersHorizontal,
  Star,
  Cpu,
  ListFilter,
  CalendarRange,
} from "lucide-react";
import { PLANNING_STAGES, PLANNING_STATUS_FILTERS } from "@apv-erp/status";
import { exportToExcel, formatDateForFile } from "@/lib/excel.utils";
import { toast } from "sonner";
import { Plus, Download } from "lucide-react";
import { useRowSelection } from "@/hooks/useRowSelection";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { BulkActionBar } from "@/components/shared/BulkActionBar";
import type { QuickDateKey } from "./components/ProcessQuickDateFilter";
import { ProcessDailyGroupedTable } from "./components/ProcessDailyGroupedTable";
import { ProcessMachineKanbanBoard } from "./components/ProcessMachineKanbanBoard";
import { ProcessGanttView } from "./components/ProcessGanttView";
import {
  cleanProcessName,
  formatProcessDuration,
  groupStepsByDate,
} from "./utils/processPlanningUtils";
import { emsTabClass } from "@/components/ui/ems-tabs";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

/** Tabs nhóm công đoạn lớn — thứ tự & nhóm lấy từ @apv-erp/status (process_groups.planning_stage) */
export const PROCESS_STAGES = [
  { key: "ALL", label: "Tất cả" },
  ...PLANNING_STAGES.map((s) => ({ key: s.key, label: s.label })),
];

/** Từ khóa phòng ban/vị trí → tab mặc định cho người dùng */
const STAGE_USER_KEYWORDS: Record<string, string[]> = {
  CUTTING: ["cắt", "xén"],
  PRINTING: ["in ấn", "offset", "printing", "thợ in", "máy in", "xưởng in"],
  LAMINATING: ["cán", "màng"],
  COATING: ["tráng", "phủ", "uv"],
  DIE_FOIL: ["bế", "ép nhũ", "die"],
  GLUING: ["dán", "bồi"],
  FINISHING: ["thành phẩm", "đóng gói", "kcs", "qc"],
};

const TIME_FILTERS: { key: QuickDateKey; label: string }[] = [
  { key: "ALL", label: "Mọi thời gian" },
  { key: "YESTERDAY", label: "Hôm qua" },
  { key: "TODAY", label: "Hôm nay" },
  { key: "TOMORROW", label: "Ngày mai" },
  { key: "THIS_WEEK", label: "Tuần này" },
  { key: "CUSTOM", label: "Tùy chọn..." },
];

const STEP_STATUS_LABELS: Record<string, string> = {
  PLANNED: "Kế hoạch",
  READY: "Sẵn sàng",
  SCHEDULED: "Đã xếp lịch",
  RUNNING: "Đang chạy",
  PAUSED: "Tạm dừng",
  COMPLETED: "Hoàn thành",
  CANCELLED: "Đã hủy",
};

export const STAGE_FILTER_STORAGE_KEY = "apv_stage_filter_v2";

export function detectUserStage(user: any): string | null {
  if (!user) return null;
  const role = (user.role || "").toLowerCase();
  if (role.includes("admin") || role.includes("director") || role.includes("manager")) {
    return null;
  }
  const dept = (user.department || "").toLowerCase();
  const sec = (user.section || "").toLowerCase();
  const pos = (user.position || "").toLowerCase();
  const combined = `${dept} ${sec} ${pos}`;

  for (const [key, keywords] of Object.entries(STAGE_USER_KEYWORDS)) {
    if (keywords.some((kw) => combined.includes(kw))) return key;
  }
  return null;
}

export function ProcessPlanningList() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);

  // Parse Search Params (Deep Linking)
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const highlightOrderParam = searchParams.get("highlightOrder") || searchParams.get("q") || "";

  // Filters & State
  const { headerSearch } = useHeaderSearch();
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [processTypeFilter, setProcessTypeFilter] = useState<string>(() => {
    try {
      const saved = typeof window !== "undefined" ? localStorage.getItem(STAGE_FILTER_STORAGE_KEY) : null;
      if (saved && PROCESS_STAGES.some((s) => s.key === saved)) {
        return saved;
      }
    } catch {
      // Ignore storage errors
    }
    const detected = detectUserStage(currentUser);
    return detected || "ALL";
  });
  const [onlyMyTasks, setOnlyMyTasks] = useState(false);
  const [machineFilter, setMachineFilter] = useState<string>("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = usePageSizeState(25);
  const [sortField, setSortField] = useState<string>("plannedStartDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [poStatusFilter, setPoStatusFilter] = useState<"ACTIVE_ONLY" | "ALL">(() => {
    const search = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    const p = search.get("poStatusFilter");
    if (p === "ACTIVE_ONLY" || p === "ALL") return p;
    if (search.get("highlightOrder") || search.get("view") === "GANTT") return "ALL";
    return "ACTIVE_ONLY";
  });
  const [viewMode, setViewMode] = useState<"BY_DATE" | "KANBAN" | "TABLE" | "GANTT">(() => {
    const v = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "").get("view");
    if (v === "GANTT" || v === "KANBAN" || v === "TABLE" || v === "BY_DATE") return v;
    return "BY_DATE";
  });
  const [quickDateKey, setQuickDateKey] = useState<QuickDateKey>("ALL");

  // Sync viewMode & poStatusFilter from URL
  React.useEffect(() => {
    const v = searchParams.get("view");
    if (v && ["BY_DATE", "KANBAN", "TABLE", "GANTT"].includes(v)) {
      setViewMode(v as any);
    }
    const p = searchParams.get("poStatusFilter");
    if (p === "ACTIVE_ONLY" || p === "ALL") {
      setPoStatusFilter(p);
    } else if (searchParams.get("highlightOrder") || v === "GANTT") {
      setPoStatusFilter("ALL");
    }
  }, [location.search]);

  // Handle Switch View Mode with replace: true (Anti-Tab Trap - Rule 3)
  const handleViewModeChange = (newMode: "BY_DATE" | "KANBAN" | "TABLE" | "GANTT") => {
    setViewMode(newMode);
    navigate({
      search: (prev: any) => ({
        ...prev,
        view: newMode,
      }),
      replace: true,
    });
  };

  // Reset page on header search change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [headerSearch]);

  // Dialog State
  const [logFormOpen, setLogFormOpen] = useState(false);
  const [selectedPrefill, setSelectedPrefill] = useState<ProductionLogPrefill | undefined>(undefined);
  const [machineModalOpen, setMachineModalOpen] = useState(false);
  const [selectedStepForMachine, setSelectedStepForMachine] = useState<any>(null);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);

  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter();

  const handleQuickDateSelect = (key: QuickDateKey) => {
    const today = new Date();
    const day = (offset: number) => format(new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset), "yyyy-MM-dd");
    const dateFrom = key === "YESTERDAY" ? day(-1) : key === "TOMORROW" ? day(1) : undefined;
    const dateTo = dateFrom;
    setQuickDateKey(key);
    setCurrentPage(1);
    if (key === "ALL") {
      setPeriod("all");
      setCustomFrom("");
      setCustomTo("");
    } else if (key === "TODAY") {
      setPeriod("today");
    } else if (key === "THIS_WEEK") {
      setPeriod("week");
    } else if (key === "CUSTOM") {
      setPeriod("custom");
    } else if (dateFrom && dateTo) {
      setPeriod("custom");
      setCustomFrom(dateFrom);
      setCustomTo(dateTo);
    }
  };

  // Effective limit: nạp 500 bản ghi khi ở chế độ Gantt để vẽ toàn cảnh xưởng
  const effectiveLimit = viewMode === "GANTT" ? 500 : pageSize;

  // Query stage work orders
  const { data, isLoading, refetch } = useQuery({
    placeholderData: (prev) => prev,
    queryKey: [
      "stage-work-orders",
      viewMode,
      currentPage,
      effectiveLimit,
      headerSearch,
      statusFilter,
      poStatusFilter,
      processTypeFilter,
      machineFilter,
      onlyMyTasks,
      dateParams.from,
      dateParams.to,
      sortField,
      sortOrder,
    ],
    queryFn: () =>
      trpcClient.productionPlanning.getStageWorkOrders.query({
        page: viewMode === "GANTT" ? 1 : currentPage,
        limit: effectiveLimit,
        search: headerSearch || undefined,
        statusGroup: statusFilter !== "ALL" ? statusFilter : undefined,
        poStatusFilter,
        stage: processTypeFilter !== "ALL" ? processTypeFilter : undefined,
        machineId: machineFilter !== "ALL" ? machineFilter : undefined,
        mine: onlyMyTasks || undefined,
        dateFrom: dateParams.from,
        dateTo: dateParams.to,
        sortBy: sortField,
        sortOrder,
      }),
  });

  // Switch stage & persist to localStorage
  const handleStageSelect = (stageKey: string) => {
    setProcessTypeFilter(stageKey);
    setCurrentPage(1);
    try {
      localStorage.setItem(STAGE_FILTER_STORAGE_KEY, stageKey);
    } catch {
      // Ignore storage errors
    }
  };

  // Sync stage if user profile loads after initial mount and no explicit storage was saved
  React.useEffect(() => {
    try {
      const saved = localStorage.getItem(STAGE_FILTER_STORAGE_KEY);
      if (!saved && currentUser) {
        const detected = detectUserStage(currentUser);
        if (detected) {
          setProcessTypeFilter(detected);
          localStorage.setItem(STAGE_FILTER_STORAGE_KEY, detected);
        }
      }
    } catch {
      // Ignore
    }
  }, [currentUser]);

  const items = data?.items || [];

  // "Việc của tôi" lọc ở server (mine=true)
  const displayedItems = items;
  const stageCounts: Record<string, number> = data?.stageCounts || {};
  const statusCounts: Record<string, number> = data?.statusCounts || {};
  const machineOptions: { id: string; name: string; code: string; count: number }[] = data?.machineOptions || [];

  // Máy đang chọn không còn công đoạn nào trong tab/bộ lọc mới → bỏ lọc máy
  React.useEffect(() => {
    if (machineFilter === "ALL" || !data) return;
    const opt = machineOptions.find((m) => m.id === machineFilter);
    if (!opt || opt.count === 0) setMachineFilter("ALL");
  }, [data, machineFilter]);

  const activeFilterCount =
    (quickDateKey !== "ALL" ? 1 : 0) + (statusFilter !== "ALL" ? 1 : 0) + (machineFilter !== "ALL" ? 1 : 0) + (onlyMyTasks ? 1 : 0);
  const clearFilters = () => {
    handleQuickDateSelect("ALL");
    setStatusFilter("ALL");
    setMachineFilter("ALL");
    setOnlyMyTasks(false);
    setCurrentPage(1);
  };

  const [isExporting, setIsExporting] = useState(false);
  const exportRows = async (rows: any[], suffix = "") => {
    const stageLabel = (k?: string | null) => PLANNING_STAGES.find((s) => s.key === k)?.label || "";
    await exportToExcel(
      rows,
      [
        { key: "poCode", label: "Mã LSX", width: 16 },
        { key: "salesOrderCode", label: "Đơn hàng", width: 16 },
        { key: "customerName", label: "Khách hàng", width: 24 },
        { key: "productName", label: "Sản phẩm", width: 36 },
        { key: "planningStage", label: "Nhóm công đoạn", width: 16, format: (v) => stageLabel(v) },
        { key: "processName", label: "Công đoạn", width: 24 },
        { key: "machineName", label: "Máy", width: 20 },
        { key: "operatorName", label: "Thợ", width: 18 },
        { key: "plannedStartDate", label: "Bắt đầu", width: 16, format: (v) => formatProcessTime(v) },
        { key: "plannedEndDate", label: "Kết thúc", width: 16, format: (v) => formatProcessTime(v) },
        { key: "standardQuantity", label: "SL kế hoạch", width: 12, type: "number" },
        { key: "actualQtyOk", label: "SL đạt", width: 12, type: "number" },
        { key: "status", label: "Trạng thái", width: 14, format: (v) => STEP_STATUS_LABELS[v] || v },
      ],
      `Ke-hoach-san-xuat${suffix}_${formatDateForFile()}`,
      { sheetName: "Kế hoạch sản xuất" }
    );
  };
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const all = await trpcClient.productionPlanning.getStageWorkOrders.query({
        page: 1,
        limit: 5000,
        search: headerSearch || undefined,
        statusGroup: statusFilter !== "ALL" ? statusFilter : undefined,
        poStatusFilter,
        stage: processTypeFilter !== "ALL" ? processTypeFilter : undefined,
        machineId: machineFilter !== "ALL" ? machineFilter : undefined,
        mine: onlyMyTasks || undefined,
        dateFrom: dateParams.from,
        dateTo: dateParams.to,
        sortBy: sortField,
        sortOrder,
      });
      await exportRows(all.items);
    } catch (e: any) {
      toast.error(e?.message || "Không xuất được Excel");
    } finally {
      setIsExporting(false);
    }
  };

  // Chọn nhiều dòng (bảng & nhóm ngày)
  const rowIds = React.useMemo(() => displayedItems.map((i: any) => i.id), [displayedItems]);
  const rowSelection = useRowSelection(rowIds);
  const selectedItems = React.useMemo(
    () => displayedItems.filter((i: any) => rowSelection.selectedIds.has(i.id)),
    [displayedItems, rowSelection.selectedIds]
  );
  const [printSelectedOnly, setPrintSelectedOnly] = useState(false);
  const selectionEnabled = viewMode === "TABLE" || viewMode === "BY_DATE";
  React.useEffect(() => {
    if (!selectionEnabled) rowSelection.clearSelection();
  }, [selectionEnabled]);

  const dayGroups = React.useMemo(() => {
    return groupStepsByDate(displayedItems);
  }, [displayedItems]);

  const meta = data?.meta || { total: 0, page: 1, limit: pageSize, totalPages: 0 };
  const summary = data?.summary || {
    totalSteps: 0,
    runningSteps: 0,
    pendingSteps: 0,
    completedSteps: 0,
    overdueSteps: 0,
    allPoStepsCount: 0,
  };

  // Open Log Form with 100% prefill
  const handleOpenLogForm = (item: any) => {
    setSelectedPrefill({
      productionOrderId: item.productionOrderId,
      poCode: item.poCode,
      productName: item.productName,
      customerName: item.customerName,
      routingStepId: item.id,
      stepName: item.name,
      stepOrder: item.stepOrder,
      machineId: item.machineId || undefined,
      machineName: item.machineName,
      standardQuantity: item.standardQuantity,
      actualQtyOk: item.actualQtyOk,
      remainingQty: item.remainingQty,
      productionUnit: item.productionUnit,
    });
    setLogFormOpen(true);
  };

  // Open Machine Assignment Modal
  const handleOpenMachineModal = (item: any) => {
    setSelectedStepForMachine({
      id: item.id,
      stepId: item.stepId,
      name: item.name,
      productionOrderId: item.productionOrderId,
      type: item.name,
      status: item.status,
      machineId: item.machineId,
      resourceId: item.machineId,
      operatorId: item.operatorId,
      qtyPlan: item.standardQuantity,
      standardQuantity: item.standardQuantity,
      overlapPercent: item.overlapPercent,
      plannedStartDate: item.plannedStartDate,
      plannedEndDate: item.plannedEndDate,
      productionOrder: {
        orderId: item.poCode,
        productName: item.productName,
        customerName: item.customerName,
      },
      poCode: item.poCode,
      productName: item.productName,
    });
    setMachineModalOpen(true);
  };

  return (
    <>
      <ListPageShell
        unitLabel="công đoạn"
        totalItems={meta.total || 0}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setCurrentPage(1);
        }}
        isLoading={isLoading}
        tabs={
          <div role="tablist" aria-label="Nhóm công đoạn" className="flex items-center gap-0 2xl:gap-0.5 min-w-max" data-testid="stage-tabs">
            {PROCESS_STAGES.map((tab) => {
              const active = processTypeFilter === tab.key;
              const count = stageCounts[tab.key] ?? 0;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-stage={tab.key}
                  onClick={() => handleStageSelect(tab.key)}
                  className={cn(emsTabClass(active, { size: "sm" }), "gap-0.5 2xl:gap-1 px-[3px] min-[1440px]:px-1.5 2xl:px-2.5 text-[11px] 2xl:text-xs")} /* 8 nhóm công đoạn: lề tab co theo bề ngang để thanh công cụ giữ 2 hàng */
                >
                  <span>{tab.label}</span>
                  <span
                    data-testid="stage-count"
                    className={cn(
                      "min-w-[1rem] px-1 rounded-full text-[10px] font-semibold tabular-nums leading-4 text-center",
                      active ? "bg-primary/10 text-primary" : count > 0 ? "bg-slate-200 text-slate-700" : "bg-slate-100 text-slate-400"
                    )}
                  >
                    {formatNumber(count)}
                  </span>
                </button>
              );
            })}
          </div>
        }
        dateFilter={
          <>
            <Select value={quickDateKey} onValueChange={(val: any) => handleQuickDateSelect(val)}>
              <SelectTrigger aria-label="Lọc thời gian" className="data-[size=default]:h-7 min-w-0 px-2 gap-1 text-xs shrink-0 bg-white border-slate-200 w-[118px]">
                <SelectValue placeholder="Thời gian" />
              </SelectTrigger>
              <SelectContent>
                {TIME_FILTERS.map((t) => (
                  <SelectItem key={t.key} value={t.key} className="text-xs">{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {quickDateKey === "CUSTOM" && (
              <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 h-7 shrink-0">
                <input
                  type="date"
                  aria-label="Từ ngày"
                  value={customFrom}
                  onChange={(e) => { setCustomFrom(e.target.value); setCurrentPage(1); }}
                  className="h-6 text-xs border-0 bg-transparent text-slate-700 focus:outline-none w-[108px]"
                />
                <span className="text-xs text-slate-400">→</span>
                <input
                  type="date"
                  aria-label="Đến ngày"
                  value={customTo}
                  onChange={(e) => { setCustomTo(e.target.value); setCurrentPage(1); }}
                  className="h-6 text-xs border-0 bg-transparent text-slate-700 focus:outline-none w-[108px]"
                />
              </div>
            )}
          </>
        }
        onExport={() => { if (!isExporting) handleExport(); }}
        exportLoading={isExporting}
        secondaryActions={[
          {
            key: "print-schedule",
            label: "In kế hoạch",
            icon: <Printer className="w-3.5 h-3.5 text-slate-600" />,
            onClick: () => { setPrintSelectedOnly(false); setIsPrintModalOpen(true); },
            title: "In kế hoạch",
          },
        ]}
        headerFilters={
          <div className="flex items-center gap-1.5 flex-nowrap">
            {/* Trạng thái — số đếm theo tab/máy/thời gian đang chọn */}
            <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setCurrentPage(1); }}>
              <SelectTrigger aria-label="Trạng thái" className="data-[size=default]:h-7 min-w-0 px-2 gap-1 text-xs shrink-0 bg-white border-slate-200 w-[150px]">
                <SelectValue placeholder="Trạng thái" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL" className="text-xs">Mọi trạng thái ({formatNumber(statusCounts.ALL ?? 0)})</SelectItem>
                {PLANNING_STATUS_FILTERS.map((f) => (
                  <SelectItem key={f.key} value={f.key} className="text-xs">
                    {f.label} ({formatNumber(statusCounts[f.key] ?? 0)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Máy — chỉ các máy có công đoạn trong tab/bộ lọc hiện tại */}
            <Select value={machineFilter} onValueChange={(val) => { setMachineFilter(val); setCurrentPage(1); }}>
              <SelectTrigger aria-label="Máy" className="data-[size=default]:h-7 min-w-0 px-2 gap-1 text-xs shrink-0 bg-white border-slate-200 w-[124px]">
                <SelectValue placeholder="Máy" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL" className="text-xs">Mọi máy</SelectItem>
                {machineOptions.map((m) => (
                  <SelectItem key={m.id} value={m.id} className="text-xs">
                    {m.name} ({formatNumber(m.count)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Phạm vi lệnh sản xuất (Gate 2) */}
            <Select value={poStatusFilter} onValueChange={(val: any) => { setPoStatusFilter(val); setCurrentPage(1); }}>
              <SelectTrigger aria-label="Phạm vi lệnh sản xuất" className="data-[size=default]:h-7 min-w-0 px-2 gap-1 text-xs shrink-0 bg-white border-slate-200 w-[116px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVE_ONLY" className="text-xs">LSX ban hành</SelectItem>
                <SelectItem value="ALL" className="text-xs">Mọi LSX</SelectItem>
              </SelectContent>
            </Select>

            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={onlyMyTasks}
              onClick={() => { setOnlyMyTasks((prev) => !prev); setCurrentPage(1); }}
              title="Chỉ công đoạn được phân công cho tôi"
              aria-label="Việc của tôi"
              className={cn(
                "h-7 px-2 text-xs shrink-0 gap-1",
                onlyMyTasks ? "bg-amber-50 border-amber-300 text-amber-800" : "text-slate-600"
              )}
            >
              <Star className={cn("w-3.5 h-3.5", onlyMyTasks ? "fill-amber-400 text-amber-500" : "text-slate-400")} />
              <span className="hidden xl:inline">Việc của tôi</span>
            </Button>

            {activeFilterCount > 0 && (
              <Button type="button" variant="ghost" size="sm" onClick={clearFilters} className="h-7 px-2 text-xs shrink-0 text-red-600 hover:bg-red-50">
                Xóa lọc ({activeFilterCount})
              </Button>
            )}
          </div>
        }
        actions={
          <div className="flex items-center gap-1.5 flex-nowrap">
            {/* Chuyển chế độ xem */}
            <div role="group" aria-label="Chế độ xem" className="inline-flex items-center bg-slate-100 p-0.5 rounded-md border border-slate-200 shrink-0">
              {[
                { key: "BY_DATE", label: "Nhóm ngày", icon: Calendar, title: "Gom nhóm công đoạn theo ngày" },
                { key: "GANTT", label: "Gantt", icon: CalendarRange, title: "Biểu đồ Gantt lịch máy" },
                { key: "KANBAN", label: "Kanban máy", icon: Cpu, title: "Kanban theo máy" },
                { key: "TABLE", label: "Bảng", icon: ListFilter, title: "Bảng chi tiết công đoạn" },
              ].map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => handleViewModeChange(v.key as any)}
                  aria-pressed={viewMode === v.key}
                  aria-label={v.label}
                  title={v.title}
                  className={cn(
                    "h-6 w-6 2xl:w-auto p-0 2xl:px-1.5 text-xs rounded transition-colors flex items-center justify-center gap-1 cursor-pointer select-none",
                    viewMode === v.key ? "bg-white text-teal-700 shadow-2xs font-semibold" : "text-slate-600 hover:text-slate-900"
                  )}
                >
                  <v.icon className="w-3.5 h-3.5" />
                  <span className="hidden 2xl:inline">{v.label}</span>
                </button>
              ))}
            </div>

            <Button size="sm" className="h-7 px-2 text-xs shrink-0 gap-1" onClick={() => navigate({ to: "/production-orders/create" })} title="Tạo lệnh sản xuất mới">
              <Plus className="w-3.5 h-3.5" />
              <span>Tạo<span className="hidden sm:inline"> LSX</span></span>
            </Button>
          </div>
        }
      >
        {viewMode === "GANTT" ? (
          <ProcessGanttView
            items={displayedItems}
            isLoading={isLoading}
            dateParams={dateParams}
            highlightOrder={highlightOrderParam}
            onNavigateToOrder={(poCode) =>
              navigate({
                to: "/production-orders/$orderId",
                params: { orderId: poCode },
              })
            }
            onNavigateToSalesOrder={(salesOrderId) =>
              navigate({
                to: "/orders/$orderId",
                params: { orderId: salesOrderId },
              })
            }
            onOpenMachineModal={handleOpenMachineModal}
            onOpenLogForm={handleOpenLogForm}
            onRefetch={refetch}
          />
        ) : isLoading ? (
          <div className="h-48 flex items-center justify-center gap-2 text-slate-500 bg-white rounded-xl border border-slate-200">
            <RefreshCw className="w-5 h-5 animate-spin text-teal-600" />
            <span>Đang tải kế hoạch công đoạn sản xuất...</span>
          </div>
        ) : displayedItems.length === 0 ? (
          <div className="h-48 flex flex-col items-center justify-center gap-1.5 bg-white rounded-xl border border-slate-200 text-slate-500">
            {onlyMyTasks ? (
              <div className="flex flex-col items-center justify-center gap-1.5 py-4">
                <Star className="w-6 h-6 text-amber-400" />
                <span className="font-semibold text-slate-700">
                  Không có công đoạn nào được phân công trực tiếp cho bạn
                </span>
                <span className="text-[11px] text-muted-foreground">
                  Bạn có thể tắt bộ lọc &quot;Việc của tôi&quot; hoặc chọn công đoạn khác để xem toàn bộ danh sách
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => setOnlyMyTasks(false)}
                >
                  Xem tất cả công đoạn
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-1.5 py-4">
                <Layers className="w-6 h-6 text-slate-300" />
                <span className="font-medium">Không tìm thấy công đoạn sản xuất phù hợp</span>
                <span className="text-[11px] text-muted-foreground">
                  {poStatusFilter === "ACTIVE_ONLY" && summary.allPoStepsCount > 0
                    ? `Đang lọc "LSX ban hành". Có ${formatNumber(summary.allPoStepsCount)} công đoạn thuộc các lệnh đang lập kế hoạch / chờ ban hành.`
                    : "Thử điều chỉnh lại bộ lọc thời gian hoặc từ khóa tìm kiếm"}
                </span>
                {poStatusFilter === "ACTIVE_ONLY" && summary.allPoStepsCount > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2 h-7 text-xs border-teal-300 text-teal-700 hover:bg-teal-50 font-semibold cursor-pointer"
                    onClick={() => {
                      setPoStatusFilter("ALL");
                      setCurrentPage(1);
                    }}
                  >
                    Xem toàn bộ {formatNumber(summary.allPoStepsCount)} công đoạn (Gồm lệnh đang lập kế hoạch)
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : viewMode === "BY_DATE" ? (
          <ProcessDailyGroupedTable
            groups={dayGroups}
            onNavigateToOrder={(poCode) =>
              navigate({
                to: "/production-orders/$orderId",
                params: { orderId: poCode },
              })
            }
            onNavigateToSalesOrder={(salesOrderId) =>
              navigate({
                to: "/orders/$orderId",
                params: { orderId: salesOrderId },
              })
            }
            onOpenLogForm={handleOpenLogForm}
            onOpenMachineModal={handleOpenMachineModal}
            rowSelection={rowSelection}
          />
        ) : viewMode === "KANBAN" ? (
          <ProcessMachineKanbanBoard
            items={displayedItems}
            allMachines={machineOptions.filter((m) => m.id !== "NONE")}
            onNavigateToOrder={(poCode) =>
              navigate({
                to: "/production-orders/$orderId",
                params: { orderId: poCode },
              })
            }
            onNavigateToSalesOrder={(salesOrderId) =>
              navigate({
                to: "/orders/$orderId",
                params: { orderId: salesOrderId },
              })
            }
            onOpenLogForm={handleOpenLogForm}
            onOpenMachineModal={handleOpenMachineModal}
          />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-xs border-collapse min-w-[1120px]">
              <thead className="sticky top-0 z-30 bg-slate-100 border-b border-slate-200 text-slate-700 select-none">
                <tr>
                  {/* Sticky Left: PO Code */}
                  <th
                    className="px-2.5 py-2 text-left font-bold w-[115px] sticky left-0 z-40 border-r border-slate-200 shadow-[2px_0_4px_-1px_rgba(0,0,0,0.06)]"
                    style={{ backgroundColor: "#f1f5f9", isolation: "isolate" }}
                  >
                    <div className="flex items-center gap-2">
                      <DataTableSelectAllCheckbox rowSelection={rowSelection} asChild />
                      <span>Mã Lệnh SX</span>
                    </div>
                  </th>
                  <th className="px-2.5 py-2 text-left font-bold w-[130px]">Đơn Hàng / Khách Hàng</th>
                  <th className="px-2.5 py-2 text-left font-bold w-[170px]">Sản Phẩm & Mã Hàng</th>
                  <th className="px-2.5 py-2 text-left font-bold w-[135px]">Công Đoạn Kế Hoạch</th>
                  <th className="px-2.5 py-2 text-left font-bold w-[125px]">Máy Phụ Trách / Thợ</th>
                  <th className="px-2.5 py-2 text-left font-bold w-[135px]">Kế Hoạch & Thời Hạn</th>
                  <th className="px-2.5 py-2 text-left font-bold w-[140px]">Số Lượng & Định Mức</th>
                  <th className="px-2.5 py-2 text-left font-bold w-[135px]">Sản Lượng Đạt / Tiến Độ</th>
                  <th className="px-2 py-2 text-center font-bold w-[95px]">Trạng Thái</th>
                  {/* Sticky Right: Actions */}
                  <th
                    className="px-2.5 py-2 text-right font-bold w-[120px] sticky right-0 z-40 border-l border-slate-200 shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)]"
                    style={{ backgroundColor: "#f1f5f9", isolation: "isolate" }}
                  >
                    Thao Tác
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {displayedItems.map((item: any, idx: number) => {
                  const isEven = idx % 2 === 0;
                  const solidRowBg = isEven ? "#ffffff" : "#f8fafc";
                  const cleanName = cleanProcessName(item.name, item.stepId, item.processGroupName);
                  const durationStr = formatProcessDuration(
                    item.estDurationHours,
                    item.plannedTotalTime,
                    item.plannedSetupTime,
                    item.plannedSpeed
                  );

                  return (
                    <DataTableRow
                      key={item.id}
                      id={item.id}
                      rowSelection={rowSelection}
                      onClick={() => handleOpenMachineModal(item)}
                      className="hover:bg-teal-50/40 transition-colors group"
                    >
                      {/* Sticky Left: PO Code & Status */}
                      <td
                        className="px-3 py-2 align-top sticky left-0 z-20 border-r border-slate-200 shadow-[2px_0_4px_-1px_rgba(0,0,0,0.06)]"
                        style={{ backgroundColor: solidRowBg, isolation: "isolate" }}
                      >
                        <div className="flex items-start gap-2">
                        <span className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                          <DataTableRowCheckbox id={item.id} rowSelection={rowSelection} asChild />
                        </span>
                        <div className="flex flex-col gap-1">
                          <span className="font-mono font-bold text-teal-700 text-left">
                            {item.poCode || item.productionOrderId.substring(0, 8)}
                          </span>
                          {item.poStatus && !["RELEASED", "IN_PROGRESS", "COMPLETED"].includes(item.poStatus) && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200/60 w-fit">
                              Chưa ban hành
                            </span>
                          )}
                        </div>
                        </div>
                      </td>

                      {/* Order & Customer */}
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-col gap-0.5">
                          {item.salesOrderCode ? (
                            <span className="font-mono font-semibold text-slate-800 text-left">
                              {item.salesOrderCode}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                          <span className="text-[11px] text-muted-foreground truncate max-w-[150px]" title={item.customerName}>
                            {item.customerName || "Khách lẻ"}
                          </span>
                        </div>
                      </td>

                      {/* Product & Print Specs Cell */}
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-col gap-1">
                          {/* Dòng 1: Tên sản phẩm */}
                          <div className="font-semibold text-slate-900 line-clamp-2 text-xs leading-snug" title={item.productName}>
                            {item.productName}
                          </div>

                          {/* Dòng 2: Cụm badges thông số in kỹ thuật */}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {item.printSize && (
                              <Badge
                                variant="outline"
                                className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] font-mono px-1.5 py-0 h-4.5"
                                title="Khổ in máy"
                              >
                                Khổ: {item.printSize}
                              </Badge>
                            )}

                            {item.ups && (
                              <Badge
                                variant="outline"
                                className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] font-mono font-bold px-1.5 py-0 h-4.5"
                                title="Số con trên tờ in"
                              >
                                {item.ups} UPS
                              </Badge>
                            )}

                            {item.layoutCode && (
                              <Badge
                                variant="outline"
                                className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-mono px-1.5 py-0 h-4.5"
                                title="Mã bài bình prepress"
                              >
                                {item.layoutCode}
                              </Badge>
                            )}
                          </div>

                          {/* Dòng 3: Quy cách xả mộc cho thợ xưởng & Mã SP */}
                          <div className="flex items-center gap-2 text-[10px] text-slate-500 font-mono flex-wrap">
                            {item.cutFormat ? (
                              <span className="text-amber-800 font-medium bg-amber-50 px-1 py-0.2 rounded border border-amber-200/50">
                                Mộc: {item.cutFormat}
                              </span>
                            ) : item.paperSpec ? (
                              <span className="text-slate-600 truncate max-w-[160px]" title={item.paperSpec}>
                                {item.paperSpec}
                              </span>
                            ) : null}

                            {item.productCode && (
                              <span className="text-slate-400">
                                #{item.productCode}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Routing Step (Clean Name - Rule 6) */}
                      <td className="px-3 py-2 align-top">
                        <div className="flex items-center gap-1.5">
                          <span className="px-1.5 py-0.2 rounded text-[10px] font-mono font-bold bg-slate-200 text-slate-800">
                            B{item.stepOrder}
                          </span>
                          <span className="font-semibold text-slate-900">
                            {cleanName}
                          </span>
                        </div>
                      </td>

                      {/* Machine & Operator */}
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-slate-800 truncate max-w-[125px]">
                            {item.machineName}
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            Thợ: {item.operatorName}
                          </span>
                        </div>
                      </td>

                      {/* Plan & Deadline */}
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-col gap-0.5 text-[10px] font-mono">
                          <span className="text-slate-500">
                            Từ: <strong className="text-slate-700">{formatProcessTime(item.plannedStartDate)}</strong>
                          </span>
                          <div className="flex items-center gap-1">
                            <span className={cn(item.isOverdue ? "text-rose-600 font-bold" : "text-slate-700")}>
                              Hạn: {formatProcessTime(item.plannedEndDate)}
                            </span>
                            {item.isOverdue && (
                              <span className="text-[9px] font-bold text-rose-600 bg-rose-50 px-1 py-0.2 rounded border border-rose-200">
                                Trễ
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Số lượng yêu cầu & Thời gian định mức */}
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-col gap-1">
                          <span className="font-mono font-bold text-indigo-700">
                            {formatNumber(item.standardQuantity)} {item.productionUnit || "tờ"}
                          </span>
                          <div className="flex items-center gap-1 text-[11px] text-slate-600">
                            <Clock className="w-3 h-3 text-slate-400" />
                            <span>Định mức: <strong>{durationStr}</strong></span>
                          </div>
                        </div>
                      </td>

                      {/* Actual Ok / Progress */}
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center justify-between text-[11px] font-mono">
                            <span className="text-emerald-600 font-bold">
                              Đạt: {formatNumber(item.actualQtyOk)}
                            </span>
                            {item.actualQtyNg > 0 && (
                              <span className="text-rose-600 font-medium">
                                Lỗi: {formatNumber(item.actualQtyNg)}
                              </span>
                            )}
                            <span className="text-slate-500 font-semibold">
                              {item.progressPercent}%
                            </span>
                          </div>
                          {/* Mini progress bar */}
                          <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                            <div
                              className={cn(
                                "h-full rounded-full transition-all duration-300",
                                item.progressPercent >= 100
                                  ? "bg-emerald-500"
                                  : item.progressPercent > 50
                                  ? "bg-teal-500"
                                  : "bg-blue-500"
                              )}
                              style={{ width: `${Math.min(100, item.progressPercent)}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-muted-foreground">
                            Còn lại: <strong className="text-slate-700">{formatNumber(item.remainingQty)}</strong>
                          </span>
                        </div>
                      </td>

                      {/* Step Status Badge */}
                      <td className="px-3 py-2 align-top text-center">
                        <ProcessStatusBadge status={item.status} />
                      </td>

                      {/* Sticky Right: Actions */}
                      <td
                        className="px-3 py-2 align-middle text-right sticky right-0 z-20 border-l border-slate-200 shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)]"
                        style={{ backgroundColor: solidRowBg, isolation: "isolate" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            size="sm"
                            className="h-7 px-2.5 text-xs font-semibold bg-teal-600 hover:bg-teal-700 text-white shadow-sm flex items-center gap-1 cursor-pointer"
                            onClick={() => handleOpenLogForm(item)}
                            title="Ghi nhận sản lượng cho công đoạn này"
                          >
                            <Zap className="w-3.5 h-3.5 fill-amber-300 text-amber-300" />
                            <span>Ghi Nhật Ký</span>
                          </Button>
                        </div>
                      </td>
                    </DataTableRow>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ListPageShell>

      {selectionEnabled && (
        <BulkActionBar
          selectedCount={rowSelection.selectedCount}
          onClearSelection={rowSelection.clearSelection}
          actions={[
            {
              label: "Xuất Excel",
              icon: <Download className="w-3.5 h-3.5" />,
              onClick: async () => {
                try {
                  await exportRows(selectedItems, "_chon");
                } catch (e: any) {
                  toast.error(e?.message || "Không xuất được Excel");
                }
              },
            },
            {
              label: "In",
              icon: <Printer className="w-3.5 h-3.5" />,
              onClick: () => {
                setPrintSelectedOnly(true);
                setIsPrintModalOpen(true);
              },
            },
          ]}
        />
      )}

      {/* Canonical Production Log Form Modal (Rule 2.1) */}
      {logFormOpen && (
        <ProductionLogForm
          open={logFormOpen}
          onOpenChange={setLogFormOpen}
          prefill={selectedPrefill}
          onSuccess={() => {
            refetch();
            queryClient.invalidateQueries({ queryKey: ["stage-work-orders"] });
            queryClient.invalidateQueries({ queryKey: ["production-logs"] });
          }}
        />
      )}

      {/* Machine Assignment Modal */}
      {machineModalOpen && (
        <MachineAssignmentModal
          isOpen={machineModalOpen}
          onClose={() => setMachineModalOpen(false)}
          step={selectedStepForMachine}
          onSuccess={() => {
            refetch();
            queryClient.invalidateQueries({ queryKey: ["stage-work-orders"] });
          }}
        />
      )}

      {/* Print Modal */}
      {isPrintModalOpen && (
        <PrintModal
          isOpen={isPrintModalOpen}
          onClose={() => setIsPrintModalOpen(false)}
          title="In Kế Hoạch Sản Xuất Công Đoạn"
          documentTitle="Ke-Hoach-San-Xuat-Cong-Doan"
          orientation="landscape"
        >
          <ProcessPlanningPrintContent
            data={printSelectedOnly && selectedItems.length > 0 ? selectedItems : items}
            viewMode="order"
          />
        </PrintModal>
      )}
    </>
  );
}

export default ProcessPlanningList;
